import { useMemo, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { PageHeader, PageLayout, Tabs } from '@buffer-mono/popcorn'

import { CreateAvatar } from '~publish/components/CreateAvatar'

import { CreateHeaderSlotsProvider } from './CreateHeaderSlots'

import styles from './CreatePage.module.css'

export type CreatePageTab = {
  value: string
  label: string
  to: string
}

type CreatePageLayoutProps = {
  currentTab: string
  tabs: CreatePageTab[]
  titleAdornments?: ReactNode
  actions?: ReactNode
  belowHeader?: ReactNode
  children: ReactNode
}

export function CreatePageLayout({
  currentTab,
  tabs,
  titleAdornments,
  actions,
  belowHeader,
  children,
}: CreatePageLayoutProps): JSX.Element {
  const [primaryAction, setPrimaryAction] = useState<HTMLElement | null>(null)
  const [tabsActions, setTabsActions] = useState<HTMLElement | null>(null)
  const [subnav, setSubnav] = useState<HTMLElement | null>(null)
  const slots = useMemo(
    () => ({ primaryAction, tabsActions, subnav }),
    [primaryAction, tabsActions, subnav],
  )

  return (
    <CreateHeaderSlotsProvider value={slots}>
      <PageLayout style={{ minHeight: '100%' }}>
        <PageHeader>
          <PageHeader.Row>
            <PageHeader.TitleGroup>
              <PageHeader.TitleLeading>
                <CreateAvatar />
              </PageHeader.TitleLeading>
              <PageHeader.Title>Create</PageHeader.Title>
              {titleAdornments ? (
                <PageHeader.TitleAdornments>
                  {titleAdornments}
                </PageHeader.TitleAdornments>
              ) : null}
            </PageHeader.TitleGroup>
            <PageHeader.Actions>
              {actions}
              <span ref={setPrimaryAction} className={styles.primaryAction} />
            </PageHeader.Actions>
          </PageHeader.Row>

          <PageHeader.Row className={styles.tabsRow}>
            <Tabs value={currentTab} asChild>
              <Tabs.TabList>
                {tabs.map((tab) => (
                  <Tabs.Tab key={tab.value} value={tab.value} asChild>
                    <NavLink to={tab.to}>{tab.label}</NavLink>
                  </Tabs.Tab>
                ))}
              </Tabs.TabList>
            </Tabs>
            <div ref={setTabsActions} className={styles.tabsActions} />
          </PageHeader.Row>
        </PageHeader>

        {belowHeader}
        <div ref={setSubnav} className={styles.subnav} />

        <PageLayout.Container size="full" className={styles.container}>
          {children}
        </PageLayout.Container>
      </PageLayout>
    </CreateHeaderSlotsProvider>
  )
}
